import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Chip, Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography, CircularProgress, Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import PublishIcon from "@mui/icons-material/Publish";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { useNavigate, useParams } from "react-router-dom";
import ApiCallService from "../../Service/ApiCallService";
import Spinner from "../../components/common/Spinner"
import { Modal, message } from "antd";

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const emptyForm = {
  program: "",
  heading: "",
  description: "",
  location: { state: "", region: "", district: "", taluka: "", village: "" },
  media: [],
};

const isAdminUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails") || "{}");
    return [
      "SuperAdmin",
      "SuperUser",
      "HeadOffice",
      "headOffice",
      "Admin",
    ].includes(user?.userRole);
  } catch {
    return false;
  }
};

const getArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getProgramName = (activity) =>
  activity?.program?.name || activity?.programName || "Program";
const getPublishedDate = (activity) =>
  activity?.publishedAt || activity?.createdAt;

const getLocationText = (activity) => {
  const location = activity?.location || {};
  return [
    location.village?.villageName,
    location.taluka?.talukaName,
    location.district?.districtName,
    location.region?.regionName,
    location.state?.StateName || location.state?.stateName,
  ]
    .filter(Boolean)
    .join(", ");
};

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const getActivityImages = (media = []) =>
  media.filter((item) => item.type?.toUpperCase() === "IMAGE");

const ActivityMedia = ({ media = [], compact = false, onOpen }) => {
  const images = getActivityImages(media);
  const video = media.find((item) => item.type?.toUpperCase() === "VIDEO");
  const primary = images[0];

  if (!primary && !video) {
    return (
      <Box
        sx={{
          aspectRatio: "16 / 9",
          bgcolor: "rgba(111,61,189,0.08)",
          display: "grid",
          placeItems: "center",
          borderRadius: 1.5,
        }}
      >
        <ImageIcon sx={{ color: "#7b4cc2", fontSize: 42 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      {primary && (
        <MotionBox
          whileHover={{ scale: compact ? 1.015 : 1.01 }}
          onClick={() => onOpen?.(0)}
          sx={{
            aspectRatio: "16 / 9",
            overflow: "hidden",
            borderRadius: 1.5,
            cursor: "pointer",
            bgcolor: "#efe8fb",
          }}
        >
          <Box
            component="img"
            src={primary.url}
            alt={primary.fileName || "Community activity"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </MotionBox>
      )}
      {video && !compact && (
        <Box
          sx={{
            position: "relative",
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "#140d1f",
          }}
        >
          <Box
            component="video"
            src={video.url}
            controls
            preload="metadata"
            sx={{ width: "100%", maxHeight: 420, display: "block" }}
          />
        </Box>
      )}
      {video && compact && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: "#5f2db0", fontWeight: 700 }}
        >
          <PlayCircleFilledWhiteIcon />
          <Typography variant="body2">Video attached</Typography>
        </Stack>
      )}
    </Box>
  );
};

const ActivityCard = ({ activity, index = 0, onClick }) => (
  <MotionPaper
    initial={{ opacity: 0, y: 28, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay: index * 0.06, duration: 0.28 }}
    whileHover={{ y: -4, boxShadow: "0 18px 38px rgba(69, 35, 116, 0.18)" }}
    onClick={onClick}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2,
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      border: "1px solid #eadff7",
      background:
        "linear-gradient(135deg, #ffffff 0%, #fbf8ff 55%, #f2eaff 100%)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        width: 130,
        height: 130,
        borderRadius: "50%",
        right: -50,
        top: -50,
        bgcolor: "rgba(111,61,189,0.08)",
      }}
    />
    <Stack spacing={1.5} sx={{ position: "relative" }}>
      <Chip
        label={getProgramName(activity)}
        size="small"
        sx={{
          alignSelf: "flex-start",
          bgcolor: "#efe7fb",
          color: "#57289d",
          fontWeight: 700,
        }}
      />
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, color: "#271b38", lineHeight: 1.25 }}
      >
        {activity.heading}
      </Typography>
      <ActivityMedia media={activity.media} compact />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {activity.description}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        color="text.secondary"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LocationOnIcon fontSize="small" />
          <Typography variant="caption">
            {getLocationText(activity) || "Location not specified"}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CalendarMonthIcon fontSize="small" />
          <Typography variant="caption">
            {formatDate(getPublishedDate(activity))}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  </MotionPaper>
);

const CommunityActivityListCard = ({ activity, index, onClick }) => {
  const image = getActivityImages(activity.media)[0];

  return (
    <MotionPaper
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.28 }}
      whileHover={{ y: -2, boxShadow: "0 12px 28px rgba(39, 54, 92, 0.12)" }}
      onClick={onClick}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "242px 1fr" },
        minHeight: { xs: 360, sm: 220 },
        overflow: "hidden",
        border: "1px solid #e8eaf2",
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          minHeight: { xs: 190, sm: "100%" },
          bgcolor: "#e9edf6",
          backgroundImage: image ? `url(${image.url})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        {!image && <ImageIcon sx={{ color: "#7b8498", fontSize: 42 }} />}
      </Box>
      <Box sx={{ p: { xs: 2, sm: 2.5 }, position: "relative", minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Chip
            label={getProgramName(activity)}
            size="small"
            sx={{ bgcolor: "#eee9ff", color: "#5d43a5", fontWeight: 700, height: 22 }}
          />
          <Stack direction="row" alignItems="center" spacing={0.5} color="#697080">
            <CalendarMonthIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{formatDate(getPublishedDate(activity))}</Typography>
          </Stack>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#202a3b", lineHeight: 1.3, mb: 1 }}>
          {activity.heading}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            overflow: "hidden",
            lineHeight: 1.5,
            mb: 1.25,
          }}
        >
          {activity.description}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} color="#707786">
          <LocationOnIcon sx={{ fontSize: 17, color: "#6b40bb" }} />
          <Typography variant="caption" noWrap>
            {getLocationText(activity) || "Location not specified"}
          </Typography>
        </Stack>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
          onClick={(event) => { event.stopPropagation(); onClick(); }}
          sx={{ position: { sm: "absolute" }, right: { sm: 20 }, bottom: { sm: 16 }, mt: { xs: 1.5, sm: 0 }, p: 0, color: "#5d299e", fontSize: 11, textTransform: "none" }}
        >
          Read Full Post
        </Button>
      </Box>
    </MotionPaper>
  );
};

const GalleryDialog = ({ images, index, onClose, onIndex }) => (
  <Dialog
    open={index >= 0}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none" } }}
  >
    <DialogContent sx={{ p: 0, position: "relative" }}>
      <AnimatePresence mode="wait">
        {index >= 0 && images[index] && (
          <MotionBox
            key={images[index].url}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            sx={{
              bgcolor: "rgba(10,6,18,0.92)",
              minHeight: "70vh",
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              p: 2,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", top: 12, right: 12, color: "white" }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              sx={{
                position: "absolute",
                top: 18,
                left: 20,
                color: "white",
                fontWeight: 700,
              }}
            >
              {index + 1} / {images.length}
            </Typography>
            {images.length > 1 && (
              <IconButton
                onClick={() =>
                  onIndex((index - 1 + images.length) % images.length)
                }
                sx={{ position: "absolute", left: 12, color: "white" }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            )}
            <Box
              component="img"
              src={images[index].url}
              alt="Community activity"
              sx={{ maxWidth: "100%", maxHeight: "82vh", borderRadius: 1.5 }}
            />
            {images.length > 1 && (
              <IconButton
                onClick={() => onIndex((index + 1) % images.length)}
                sx={{ position: "absolute", right: 12, color: "white" }}
              >
                <NavigateNextIcon />
              </IconButton>
            )}
          </MotionBox>
        )}
      </AnimatePresence>
    </DialogContent>
  </Dialog>
);

const ActivitySkeleton = () => (
  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
    <MotionBox
      animate={{ opacity: [0.45, 0.9, 0.45] }}
      transition={{ repeat: Infinity, duration: 1.4 }}
      sx={{ display: "grid", gap: 1.5 }}
    >
      <Box
        sx={{ width: 120, height: 24, bgcolor: "#eee8f7", borderRadius: 4 }}
      />
      <Box sx={{ height: 28, bgcolor: "#eee8f7", borderRadius: 1 }} />
      <Box
        sx={{ aspectRatio: "16 / 9", bgcolor: "#eee8f7", borderRadius: 1.5 }}
      />
      <Box sx={{ height: 16, bgcolor: "#eee8f7", borderRadius: 1 }} />
    </MotionBox>
  </Paper>
);

const CommunityActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const loadActivities = async () => {
    try {
      setError("");
      setLoading(true);
      const response = await ApiCallService.get("/community-activities", {
        limit: 50,
      });
      setActivities(getArray(response));
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to load Community Activities.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fe" }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "#eef3ff",
          pt: { xs: 4, md: 5 },
          pb: { xs: 5, md: 6 },
          "&::before": {
            content: '""',
            position: "absolute",
            width: "62%",
            height: 110,
            top: -66,
            left: -20,
            bgcolor: "#d9ddf4",
            borderRadius: "0 0 55% 45%",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: "58%",
            height: 90,
            top: -54,
            right: -90,
            bgcolor: "#d9ddf4",
            borderRadius: "0 0 0 60%",
          },
        }}
      >
        <Box sx={{ maxWidth: 1120, mx: "auto", px: { xs: 2, md: 4 }, position: "relative", zIndex: 1 }}>
          <MotionBox
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{ mb: 0 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 900, color: "#0d1b32", letterSpacing: 0 }}>
                  Community Activity
                </Typography>
                <Typography color="text.secondary">
                  Stay connected with what's happening across our programs and communities.
                </Typography>
              </Box>

              {isAdminUser() && (
                <Button
                  variant="contained"
                  onClick={() => navigate("/admin-community-activity")}
                >
                  Manage Activities
                </Button>
              )}
            </Stack>
          </MotionBox>
        </Box>
      </Box>
      <Box sx={{ maxWidth: 1120, mx: "auto", px: { xs: 2, md: 4 }, mt: { xs: -2, md: -3 }, position: "relative", zIndex: 2 }}>
        {error && (
          <Alert
            severity="error"
            action={<Button onClick={loadActivities}>Retry</Button>}
          >
            {error}
          </Alert>
        )}
        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <ActivitySkeleton key={i} />
            ))}
          </Stack>
        ) : activities.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800}>
              No Community Activities Yet
            </Typography>
            <Typography color="text.secondary">
              Community activities will appear here once they are published.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {activities.map((activity, index) => (
              <CommunityActivityListCard
                key={activity._id}
                activity={activity}
                index={index}
                onClick={() => navigate(`/community-activity/${activity._id}`)}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const CommunityActivityDetail = () => {
  const { id } = useParams();

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [galleryIndex, setGalleryIndex] = useState(-1);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await ApiCallService.get(
          `/community-activities/${id}`
        );

        if (!active) return;

        setActivity(response?.data || response);

        // Mark activity as read
        ApiCallService.post(`/community-activities/${id}/read`).catch(
          () => {}
        );
      } catch (err) {
        if (active) {
          setError(
            err?.response?.data?.message ||
              "Unable to load Community Activity."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [id]);

  const images = useMemo(
    () => getActivityImages(activity?.media),
    [activity]
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#faf8fc",
        }}
      >
        <Spinner loading={loading} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          backgroundColor: "#faf8fc",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
          }}
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  if (!activity) return null;

  const programName = getProgramName(activity);
  const publishedDate = formatDate(getPublishedDate(activity));
  const location =
    getLocationText(activity) || "Location not specified";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#faf8fc",
        px: {
          xs: 2,
          sm: 3,
          md: 4,
          lg: 5,
        },
        py: {
          xs: 2.5,
          sm: 3.5,
          md: 5,
        },
      }}
    >
      <Spinner loading={loading} />

      <Box
        sx={{
          width: "100%",
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        <MotionBox
          initial={{
            opacity: 0,
            y: 24,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              mb: {
                xs: 3,
                md: 4,
              },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                color: "#221331",
                fontSize: {
                  xs: "28px",
                  sm: "34px",
                  md: "42px",
                  lg: "48px",
                },
                lineHeight: 1.15,
                letterSpacing: "-0.5px",
                maxWidth: 1200,
                mb: {
                  xs: 2,
                  md: 2.5,
                },
              }}
            >
              {activity.heading}
            </Typography>

            {/* Activity Meta Information */}
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={{
                xs: 1.5,
                sm: 2.5,
                md: 3,
              }}
              alignItems={{
                xs: "flex-start",
                sm: "center",
              }}
              sx={{
                color: "text.secondary",
                flexWrap: "wrap",
              }}
            >
              {/* Program */}
              <Chip
                label={programName}
                sx={{
                  height: 34,
                  bgcolor: "#efe7fb",
                  color: "#57289d",
                  fontWeight: 800,
                  fontSize: 14,
                  borderRadius: 1.5,
                  px: 0.5,
                  "& .MuiChip-label": {
                    px: 1.2,
                  },
                }}
              />

              {/* Date */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
              >
                <CalendarMonthIcon
                  sx={{
                    fontSize: 20,
                    color: "#6f5a7e",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#5f5367",
                  }}
                >
                  {publishedDate}
                </Typography>
              </Stack>

              {/* Location */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{
                  minWidth: 0,
                }}
              >
                <LocationOnIcon
                  sx={{
                    fontSize: 20,
                    color: "#6f5a7e",
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#5f5367",
                    wordBreak: "break-word",
                  }}
                >
                  {location}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1.15fr) minmax(0, 0.85fr)",
              },
              gap: {
                xs: 4,
                md: 5,
                lg: 7,
              },
              alignItems: "start",
            }}
          >
            {/* Left Side - Media */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  borderRadius: {
                    xs: 2,
                    md: 3,
                  },
                }}
              >
                <ActivityMedia
                  media={activity.media}
                  onOpen={setGalleryIndex}
                />
              </Box>

              {/* Image Thumbnails */}
              {images.length > 1 && (
                <Box
                  sx={{
                    width: "100%",
                    mt: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.2}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: 1,

                      "&::-webkit-scrollbar": {
                        height: 5,
                      },

                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#d5c6df",
                        borderRadius: 10,
                      },

                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f3eef6",
                        borderRadius: 10,
                      },

                      scrollbarWidth: "thin",
                    }}
                  >
                    {images.map((image, idx) => (
                      <Box
                        key={image.url}
                        component="img"
                        src={image.url}
                        alt={`Activity image ${idx + 1}`}
                        onClick={() => setGalleryIndex(idx)}
                        sx={{
                          width: {
                            xs: 88,
                            sm: 100,
                            md: 110,
                            lg: 120,
                          },
                          height: {
                            xs: 60,
                            sm: 68,
                            md: 74,
                            lg: 80,
                          },
                          flexShrink: 0,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          cursor: "pointer",
                          border: "2px solid #dac8f3",
                          transition:
                            "transform 0.2s ease, border-color 0.2s ease, opacity 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            borderColor: "#57289d",
                            opacity: 0.9,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Right Side - Description */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                pt: {
                  xs: 0,
                  md: 0.5,
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#221331",
                  fontSize: {
                    xs: "22px",
                    md: "26px",
                  },
                  lineHeight: 1.3,
                  mb: 1.5,
                }}
              >
                Activity Description
              </Typography>

              {/* Small Divider */}
              <Box
                sx={{
                  width: 50,
                  height: 3,
                  backgroundColor: "#57289d",
                  borderRadius: 10,
                  mb: 3,
                }}
              />

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: {
                    xs: 16,
                    md: 17,
                  },
                  lineHeight: 1.85,
                  whiteSpace: "pre-line",
                  color: "#332640",
                  wordBreak: "break-word",
                }}
              >
                {activity.description}
              </Typography>
            </Box>
          </Box>
        </MotionBox>
      </Box>

      {/* Gallery Dialog */}
      <GalleryDialog
        images={images}
        index={galleryIndex}
        onClose={() => setGalleryIndex(-1)}
        onIndex={setGalleryIndex}
      />
    </Box>
  );
};




const CommunityActivityAdmin = () => {
  const [form, setForm] = useState(emptyForm);
  const [activities, setActivities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState({
    states: [],
    regions: [],
    districts: [],
    talukas: [],
    villages: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadActivities = async () => {
    const response = await ApiCallService.get("/community-activities", {
      includeRemoved: true,
      limit: 100,
    });
    setActivities(getArray(response));
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [activityRes, programRes, stateRes] = await Promise.all([
          ApiCallService.get("/community-activities", {
            includeRemoved: true,
            limit: 100,
          }),
          ApiCallService.get("/programs"),
          ApiCallService.get("/states"),
        ]);
        setActivities(getArray(activityRes));
        setPrograms(getArray(programRes));
        setLocations((prev) => ({ ...prev, states: getArray(stateRes) }));
      } catch (err) {
        setError(
          err?.response?.data?.message ||
          "Unable to load Community Activity admin data.",
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!form.location.state) return;
    ApiCallService.get(`/state/${form.location.state}/region`)
      .then((res) =>
        setLocations((prev) => ({ ...prev, regions: getArray(res) })),
      )
      .catch(() => { });
    ApiCallService.get(`/state/${form.location.state}/district`)
      .then((res) =>
        setLocations((prev) => ({ ...prev, districts: getArray(res) })),
      )
      .catch(() => { });
  }, [form.location.state]);

  useEffect(() => {
    if (!form.location.district) return;
    ApiCallService.get(`/district/${form.location.district}/taluka`)
      .then((res) =>
        setLocations((prev) => ({ ...prev, talukas: getArray(res) })),
      )
      .catch(() => { });
  }, [form.location.district]);

  // useEffect(() => {
  //   if (!form.location.taluka) return;
  //   ApiCallService.get(`/taluka/${form.location.taluka}/village`)
  //     .then((res) =>
  //       setLocations((prev) => ({ ...prev, villages: getArray(res) })),
  //     )
  //     .catch(() => {});
  // }, [form.location.taluka]);

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));
  const setLocation = (name, value) =>
    setForm((prev) => ({
      ...prev,
      location: { ...prev.location, [name]: value },
    }));

  const uploadFile = async (file, type) => {
    const data = new FormData();
    data.append("file", file);
    const endpoint = type === "IMAGE" ? "/upload/image" : "/upload/media";
    const response = await ApiCallService.post(endpoint, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      type,
      url: response?.document || response?.url || response?.data?.url,
      fileName: file.name,
    };
  };

  const onFiles = async (event, type) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSaving(true);
    try {
      const uploaded = await Promise.all(
        files.map((file) => uploadFile(file, type)),
      );
      setForm((prev) => ({
        ...prev,
        media: [...prev.media, ...uploaded.filter((item) => item.url)],
      }));
    } catch (err) {
      setError(err?.response?.data?.message || "Media upload failed.");
    } finally {
      setSaving(false);
      event.target.value = "";
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async () => {
    if (!form.program || !form.heading.trim() || !form.description.trim()) {
      setError("Program, heading and description are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId)
        await ApiCallService.put(`/community-activities/${editingId}`, form);
      else await ApiCallService.post("/community-activities", form);
      resetForm();
      await loadActivities();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to publish community activity.",
      );
    } finally {
      setSaving(false);
    }
  };

  const edit = (activity) => {
    setEditingId(activity._id);
    setForm({
      program: activity.program?._id || activity.program || "",
      heading: activity.heading || "",
      description: activity.description || "",
      location: {
        state: activity.location?.state?._id || activity.location?.state || "",
        region:
          activity.location?.region?._id || activity.location?.region || "",
        district:
          activity.location?.district?._id || activity.location?.district || "",
        // taluka:
        //   activity.location?.taluka?._id || activity.location?.taluka || "",
        // village:
        //   activity.location?.village?._id || activity.location?.village || "",
      },
      media: activity.media || [],
    });
  };





  const remove = async (id) => {
    Modal.confirm({
      title: "Remove Community Activity?",
      content: "Are you sure you want to remove this community activity?",
      okText: "Remove",
      cancelText: "Cancel",
      okType: "danger",

      styles: {
        content: {
          backgroundColor: "#fff",
        },
        header: {
          backgroundColor: "#fff",
        },
        body: {
          backgroundColor: "#fff",
        },
        footer: {
          backgroundColor: "#1616e0",
        },
      },

      onOk: async () => {
        try {
          await ApiCallService.delete(`/community-activities/${id}`);
          message.success("Community activity removed successfully");
          await loadActivities();
        } catch (error) {
          message.error("Failed to remove community activity");
        }
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1180, mx: "auto" }}>
      <Spinner loading={loading} />
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
        Community Activity
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Create, preview and publish community activities.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{ p: 3, borderRadius: 2 }}
          >
            <Stack spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Program</InputLabel>
                <Select
                  label="Program"
                  value={form.program}
                  onChange={(e) => setField("program", e.target.value)}
                >
                  {programs.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Grid container spacing={2}>
                {[
                  ["state", "State", locations.states, "StateName"],
                  ["region", "Region", locations.regions, "regionName"],
                  ["district", "District", locations.districts, "districtName"],
                  // ["taluka", "Taluka", locations.talukas, "talukaName"],
                  // ["village", "Village", locations.villages, "villageName"],
                ].map(([key, label, options, textKey]) => (
                  <Grid item xs={12} sm={key === "village" ? 12 : 6} key={key}>
                    <FormControl fullWidth>
                      <InputLabel>{label}</InputLabel>
                      <Select
                        label={label}
                        value={form.location[key]}
                        onChange={(e) => setLocation(key, e.target.value)}
                      >
                        {options.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item[textKey] || item.stateName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
              <TextField
                required
                label="Heading"
                value={form.heading}
                onChange={(e) => setField("heading", e.target.value)}
                fullWidth
              />
              <TextField
                required
                label="Description"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                multiline
                minRows={5}
                fullWidth
              />
              <Paper
                variant="outlined"
                sx={{ p: 2, borderStyle: "dashed", borderColor: "#cdbce8" }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    component="label"
                    startIcon={<ImageIcon />}
                    variant="outlined"
                  >
                    Upload Photos
                    <input
                      hidden
                      multiple
                      accept="image/*"
                      type="file"
                      onChange={(e) => onFiles(e, "IMAGE")}
                    />
                  </Button>
                  <Button
                    component="label"
                    startIcon={<VideoLibraryIcon />}
                    variant="outlined"
                    disabled={form.media.some((m) => m.type === "VIDEO")}
                  >
                    Upload Video
                    <input
                      hidden
                      accept="video/*"
                      type="file"
                      onChange={(e) => onFiles(e, "VIDEO")}
                    />
                  </Button>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2, flexWrap: "wrap" }}
                >
                  {form.media.map((item, index) => (
                    <Chip
                      key={`${item.url}-${index}`}
                      label={item.fileName || item.type}
                      onDelete={() =>
                        setForm((prev) => ({
                          ...prev,
                          media: prev.media.filter((_, i) => i !== index),
                        }))
                      }
                    />
                  ))}
                </Stack>
              </Paper>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<PublishIcon />}
                  disabled={saving}
                  onClick={submit}
                >
                  {editingId ? "Update Activity" : "Publish Activity"}
                </Button>
                {editingId && <Button onClick={resetForm}>Cancel Edit</Button>}
              </Stack>
            </Stack>
          </MotionPaper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>
            Preview
          </Typography>
          <ActivityCard
            activity={{
              ...form,
              program: programs.find((p) => p._id === form.program) || {
                name: "Program",
              },
              publishedAt: new Date(),
            }}
          />
        </Grid>
      </Grid>
      <Stack spacing={1.5} sx={{ mt: 4 }}>
        {activities.map((activity) => (
          <Paper
            key={activity._id}
            sx={{
              p: 2,
              borderRadius: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography fontWeight={800}>{activity.heading}</Typography>
              <Typography variant="body2" color="text.secondary">
                {getProgramName(activity)} •{" "}
                {formatDate(getPublishedDate(activity))} • {activity.status}
              </Typography>
            </Box>
            <Stack direction="row">
              <IconButton onClick={() => edit(activity)}>
                <EditIcon />
              </IconButton>
              <IconButton color="error" onClick={() => remove(activity._id)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

const CommunityActivity = ({ admin = false, detail = false }) => {
  if (admin) return <CommunityActivityAdmin />;
  if (detail) return <CommunityActivityDetail />;
  return <CommunityActivityFeed />;
};

export default CommunityActivity;
